import { Container } from '@blocksuite/affine/global/di';
import type {
  AttachmentBlockModel,
  BookmarkBlockModel,
  EmbedBlockModel,
  ImageBlockModel,
  TableBlockModel,
} from '@blocksuite/affine/model';
import { AffineSchemas } from '@blocksuite/affine/schemas';
import { MarkdownAdapter } from '@blocksuite/affine/shared/adapters';
import type { AffineTextAttributes } from '@blocksuite/affine/shared/types';
import {
  createYProxy,
  type DeltaInsert,
  type DraftModel,
  Schema,
  Transformer,
  type TransformerMiddleware,
  type YBlock,
} from '@blocksuite/affine/store';
import {
  assertPagePartsLoaded,
  type PageFieldLookup,
  pagePlacedBlockIds,
  pagePlacedElementIds,
  type PageReadSource,
  pageTreeChildLists,
  pageTreeElementLists,
  pageTreeMemberLists,
  readDatabaseShardDirectoryBlockHead,
  readPage,
} from '@streamlain/database-identity';
import {
  extractSearchBlocks,
  hiddenBlockIds,
  type SearchBlock,
  type SearchBlocksJson,
} from '@streamlain/search-extract';
import { uniq } from 'lodash-es';
import {
  Array as YArray,
  type Doc as YDoc,
  Map as YMap,
  Text as YText,
} from 'yjs';

import { getStoreManager } from './bs-store';
import { readerDatabaseCells } from './doc-parser/database-cells';
import type { SerializedCells, ShardRowLoader } from './doc-parser/types';
import { toSearchJson } from './search-json';

const blocksuiteSchema = new Schema();
blocksuiteSchema.register([...AffineSchemas]);

export interface BlockDocumentInfo {
  docId: string;
  blockId: string;
  content?: string | string[];
  flavour: string;
  sourceType?: SearchSourceType;
  blob?: string[];
  refDocId?: string[];
  ref?: string[];
  parentFlavour?: string;
  parentBlockId?: string;
  additional?: SearchBlock['additional'];
  yblock: YMap<any>;
  markdownPreview?: string;
}

export type SearchSourceType =
  | 'doc'
  | 'text'
  | 'image'
  | 'edgeless'
  | 'attachment'
  | 'embed'
  | 'database'
  | 'pdf';

const bookmarkFlavours = new Set([
  'affine:bookmark',
  'affine:embed-linked-doc',
  'affine:embed-synced-doc',
  'affine:embed-youtube',
  'affine:embed-figma',
  'affine:embed-github',
  'affine:embed-loom',
]);

export function classifyBlockSourceType(
  flavour: string | undefined
): SearchSourceType {
  if (flavour?.startsWith('edgeless:')) {
    return 'edgeless';
  }

  switch (flavour) {
    case 'affine:image':
      return 'image';
    case 'affine:surface':
      return 'edgeless';
    case 'affine:attachment':
      return 'attachment';
    case 'affine:database':
    case 'affine:table':
      return 'database';
    case 'affine:page':
    case undefined:
    case '':
      return 'doc';
    default:
      return bookmarkFlavours.has(flavour ?? '') ? 'embed' : 'text';
  }
}

/** The Y block, or for a canvas element its Y map, of a search block. */
function searchYBlock(
  blocks: PageFieldLookup<unknown>,
  elements: PageFieldLookup<unknown>,
  searchBlock: SearchBlock
): YMap<any> | null {
  const source = searchBlock.flavour.startsWith('edgeless:')
    ? elements
    : blocks;
  const block = source.get(searchBlock.blockId);
  return block instanceof YMap ? block : null;
}

/** Navigation params of the pages a block references, by page id. */
function referenceParams(yblock: YMap<any>) {
  const params = new Map<string, Record<string, unknown>>();
  const text = yblock.get('prop:text');
  if (text instanceof YText) {
    const deltas: DeltaInsert<AffineTextAttributes>[] = text.toDelta();
    for (const { attributes } of deltas) {
      const reference = attributes?.reference;
      if (reference?.pageId && !params.has(reference.pageId)) {
        params.set(reference.pageId, reference.params ?? {});
      }
    }
  }
  const pageId = yblock.get('prop:pageId');
  if (typeof pageId === 'string') {
    params.set(pageId, yblock.get('prop:params') ?? {});
  }
  return params;
}

function generateMarkdownPreviewBuilder(
  workspaceId: string,
  blocks: BlockDocumentInfo[],
  yRootDoc?: YDoc
) {
  function yblockToDraftModal(yblock: YBlock): DraftModel | null {
    const flavour = yblock.get('sys:flavour') as string;
    const blockSchema = blocksuiteSchema.flavourSchemaMap.get(flavour);
    if (!blockSchema) {
      return null;
    }
    const keys = Array.from(yblock.keys())
      .filter(key => key.startsWith('prop:'))
      .map(key => key.substring(5));

    const props = Object.fromEntries(
      keys.map(key => [key, createYProxy(yblock.get(`prop:${key}`))])
    );

    return {
      props,
      id: yblock.get('sys:id') as string,
      flavour,
      children: [],
      role: blockSchema.model.role,
      version: (yblock.get('sys:version') as number) ?? blockSchema.version,
      keys: Array.from(yblock.keys())
        .filter(key => key.startsWith('prop:'))
        .map(key => key.substring(5)),
    } as unknown as DraftModel;
  }

  const titleMiddleware: TransformerMiddleware = ({ adapterConfigs }) => {
    const pages = yRootDoc?.getMap('meta').get('pages');
    if (!(pages instanceof YArray)) {
      return;
    }
    for (const meta of pages.toArray()) {
      adapterConfigs.set(
        'title:' + meta.get('id'),
        meta.get('title')?.toString() ?? 'Untitled'
      );
    }
  };

  const baseUrl = `/workspace/${workspaceId}`;

  function getDocLink(docId: string, blockId: string) {
    const searchParams = new URLSearchParams();
    searchParams.set('blockIds', blockId);
    return `${baseUrl}/${docId}?${searchParams.toString()}`;
  }

  const docLinkBaseURLMiddleware: TransformerMiddleware = ({
    adapterConfigs,
  }) => {
    adapterConfigs.set('docLinkBaseUrl', baseUrl);
  };

  const container = new Container();
  getStoreManager()
    .get('store')
    .forEach(ext => {
      ext.setup(container);
    });

  const provider = container.provider();
  const markdownAdapter = new MarkdownAdapter(
    new Transformer({
      schema: blocksuiteSchema,
      blobCRUD: {
        delete: () => Promise.resolve(),
        get: () => Promise.resolve(null),
        list: () => Promise.resolve([]),
        set: () => Promise.resolve(''),
      },
      docCRUD: {
        create: () => {
          throw new Error('Not implemented');
        },
        get: () => null,
        delete: () => {},
      },
      middlewares: [docLinkBaseURLMiddleware, titleMiddleware],
    }),
    provider
  );

  const markdownPreviewCache = new WeakMap<BlockDocumentInfo, string | null>();

  function trimCodeBlock(markdown: string) {
    const lines = markdown.split('\n').filter(line => line.trim() !== '');
    if (lines.length > 5) {
      return [...lines.slice(0, 4), '...', lines.at(-1), ''].join('\n');
    }
    return [...lines, ''].join('\n');
  }

  function trimParagraph(markdown: string) {
    const lines = markdown.split('\n').filter(line => line.trim() !== '');

    if (lines.length > 3) {
      return [...lines.slice(0, 3), '...', lines.at(-1), ''].join('\n');
    }

    return [...lines, ''].join('\n');
  }

  function getListDepth(block: BlockDocumentInfo) {
    let parentBlockCount = 0;
    let currentBlock: BlockDocumentInfo | undefined = block;
    do {
      currentBlock = blocks.find(
        b => b.blockId === currentBlock?.parentBlockId
      );

      // reach the root block. do not count it.
      if (!currentBlock || currentBlock.flavour !== 'affine:list') {
        break;
      }
      parentBlockCount++;
    } while (currentBlock);
    return parentBlockCount;
  }

  // only works for list block
  function indentMarkdown(markdown: string, depth: number) {
    if (depth <= 0) {
      return markdown;
    }

    return (
      markdown
        .split('\n')
        .map(line => '    '.repeat(depth) + line)
        .join('\n') + '\n'
    );
  }

  const generateDatabaseMarkdownPreview = (block: BlockDocumentInfo) => {
    const isDatabaseBlock = (block: BlockDocumentInfo) => {
      return block.flavour === 'affine:database';
    };

    const model = yblockToDraftModal(block.yblock);

    if (!model) {
      return null;
    }

    let dbBlock: BlockDocumentInfo | null = null;

    if (isDatabaseBlock(block)) {
      dbBlock = block;
    } else {
      const parentBlock = blocks.find(b => b.blockId === block.parentBlockId);

      if (parentBlock && isDatabaseBlock(parentBlock)) {
        dbBlock = parentBlock;
      }
    }

    if (!dbBlock) {
      return null;
    }

    const title = dbBlock.additional?.databaseName;

    return `${title || 'Untitled'}\n`;
  };

  const generateImageMarkdownPreview = (block: BlockDocumentInfo) => {
    const isImageModel = (
      model: DraftModel | null
    ): model is DraftModel<ImageBlockModel> => {
      return model?.flavour === 'affine:image';
    };

    const model = yblockToDraftModal(block.yblock);

    if (!isImageModel(model)) {
      return null;
    }

    const info = ['an image block'];

    if (model.props.sourceId) {
      info.push(`file id ${model.props.sourceId}`);
    }

    if (model.props.caption) {
      info.push(`with caption ${model.props.caption}`);
    }

    return info.join(', ') + '\n';
  };

  const generateEmbedMarkdownPreview = (block: BlockDocumentInfo) => {
    const isEmbedModel = (
      model: DraftModel | null
    ): model is DraftModel<EmbedBlockModel> => {
      return (
        model?.flavour === 'affine:embed-linked-doc' ||
        model?.flavour === 'affine:embed-synced-doc'
      );
    };

    const draftModel = yblockToDraftModal(block.yblock);
    if (!isEmbedModel(draftModel)) {
      return null;
    }

    const url = getDocLink(block.docId, draftModel.id);

    return `[](${url})\n`;
  };

  const generateLatexMarkdownPreview = (block: BlockDocumentInfo) => {
    let content =
      typeof block.content === 'string'
        ? block.content.trim()
        : block.content?.join('').trim();

    content = content?.split('\n').join(' ') ?? '';

    return `LaTeX, with value ${content}\n`;
  };

  const generateBookmarkMarkdownPreview = (block: BlockDocumentInfo) => {
    const isBookmarkModel = (
      model: DraftModel | null
    ): model is DraftModel<BookmarkBlockModel> => {
      return bookmarkFlavours.has(model?.flavour ?? '');
    };

    const draftModel = yblockToDraftModal(block.yblock);
    if (!isBookmarkModel(draftModel)) {
      return null;
    }
    const title = draftModel.props.title;
    const url = draftModel.props.url;
    return `[${title}](${url})\n`;
  };

  const generateAttachmentMarkdownPreview = (block: BlockDocumentInfo) => {
    const isAttachmentModel = (
      model: DraftModel | null
    ): model is DraftModel<AttachmentBlockModel> => {
      return model?.flavour === 'affine:attachment';
    };

    const draftModel = yblockToDraftModal(block.yblock);
    if (!isAttachmentModel(draftModel)) {
      return null;
    }

    return `[${draftModel.props.name}](${draftModel.props.sourceId})\n`;
  };

  const generateTableMarkdownPreview = (block: BlockDocumentInfo) => {
    const isTableModel = (
      model: DraftModel | null
    ): model is DraftModel<TableBlockModel> => {
      return model?.flavour === 'affine:table';
    };

    const draftModel = yblockToDraftModal(block.yblock);
    if (!isTableModel(draftModel)) {
      return null;
    }

    const url = getDocLink(block.docId, draftModel.id);

    return `[table][](${url})\n`;
  };

  const generateMarkdownPreview = async (block: BlockDocumentInfo) => {
    if (markdownPreviewCache.has(block)) {
      return markdownPreviewCache.get(block);
    }
    const flavour = block.flavour;
    let markdown: string | null = null;

    if (
      flavour === 'affine:paragraph' ||
      flavour === 'affine:list' ||
      flavour === 'affine:code'
    ) {
      const draftModel = yblockToDraftModal(block.yblock);
      markdown =
        block.parentFlavour === 'affine:database'
          ? generateDatabaseMarkdownPreview(block)
          : ((draftModel ? await markdownAdapter.fromBlock(draftModel) : null)
              ?.file ?? null);

      if (markdown) {
        if (flavour === 'affine:code') {
          markdown = trimCodeBlock(markdown);
        } else if (flavour === 'affine:paragraph') {
          markdown = trimParagraph(markdown);
        }
      }
    } else if (flavour === 'affine:database') {
      markdown = generateDatabaseMarkdownPreview(block);
    } else if (
      flavour === 'affine:embed-linked-doc' ||
      flavour === 'affine:embed-synced-doc'
    ) {
      markdown = generateEmbedMarkdownPreview(block);
    } else if (flavour === 'affine:attachment') {
      markdown = generateAttachmentMarkdownPreview(block);
    } else if (flavour === 'affine:image') {
      markdown = generateImageMarkdownPreview(block);
    } else if (flavour === 'affine:surface' || flavour === 'affine:page') {
      // skip
    } else if (flavour === 'affine:latex') {
      markdown = generateLatexMarkdownPreview(block);
    } else if (bookmarkFlavours.has(flavour)) {
      markdown = generateBookmarkMarkdownPreview(block);
    } else if (flavour === 'affine:table') {
      markdown = generateTableMarkdownPreview(block);
    } else {
      console.warn(`unknown flavour: ${flavour}`);
    }

    if (markdown && flavour === 'affine:list') {
      const blockDepth = getListDepth(block);
      markdown = indentMarkdown(markdown, Math.max(0, blockDepth));
    }

    markdownPreviewCache.set(block, markdown);
    return markdown;
  };

  return generateMarkdownPreview;
}

// remove the indent of the first line of list
// e.g.,
// ```
//     - list item 1
//       - list item 2
// ```
// becomes
// ```
// - list item 1
//   - list item 2
// ```
function unindentMarkdown(markdown: string) {
  const lines = markdown.split('\n');
  const res: string[] = [];
  let firstListFound = false;
  let baseIndent = 0;

  for (let current of lines) {
    const indent = current.match(/^\s*/)?.[0]?.length ?? 0;

    if (indent > 0) {
      if (!firstListFound) {
        // For the first list item, remove all indentation
        firstListFound = true;
        baseIndent = indent;
        current = current.trimStart();
      } else {
        // For subsequent list items, maintain relative indentation
        current =
          ' '.repeat(Math.max(0, indent - baseIndent)) + current.trimStart();
      }
    }

    res.push(current);
  }

  return res.join('\n');
}

export async function readAllBlocksFromDoc({
  page: source,
  rootYDoc,
  spaceId,
  maxSummaryLength,
  shardRowLoader,
}: {
  /** The page with every part its entries name. */
  page: PageReadSource;
  rootYDoc?: YDoc;
  spaceId: string;
  maxSummaryLength?: number;
  shardRowLoader?: ShardRowLoader;
}): Promise<
  | {
      blocks: BlockDocumentInfo[];
      title: string;
      summary: string;
    }
  | undefined
> {
  let docTitle = '';
  let summary = '';
  maxSummaryLength ??= 1000;
  const blockDocuments: BlockDocumentInfo[] = [];

  const generateMarkdownPreview = generateMarkdownPreviewBuilder(
    spaceId,
    blockDocuments,
    rootYDoc
  );

  // Indexing and previews read the whole page: every part.
  assertPagePartsLoaded(source.head, source.loadedPartIds());
  const read = readPage(source);
  const { entries, tree, elements } = read;
  // Block fields carry the flavour-typed props the parsers read.
  const blocks = read.blocks as PageFieldLookup<YMap<any>>;
  if (entries.size === 0) {
    return undefined;
  }

  // build a parent map for quick lookup
  // for each block, record its parent id
  const children = pageTreeChildLists(tree);
  const parentMap: Record<string, string | null> = {};
  for (const [id, childIds] of Object.entries(children)) {
    for (const child of childIds) {
      parentMap[child] = id;
    }
  }

  // find the nearest block that satisfies the predicate
  const nearest = (
    blockId: string,
    predicate: (block: YMap<any>) => boolean
  ) => {
    let current: string | null = blockId;
    while (current) {
      const block = blocks.get(current);
      if (block && predicate(block)) {
        return block;
      }
      current = parentMap[current] ?? null;
    }
    return null;
  };

  const nearestByFlavour = (blockId: string, flavour: string) =>
    nearest(blockId, block => block.get('sys:flavour') === flavour);

  const rootBlockId = tree.root;
  const placedBlockIds = pagePlacedBlockIds(tree);

  if (!rootBlockId) {
    return undefined;
  }

  // #region first loop - generate block base info
  const page = {
    blocks: Object.fromEntries(
      placedBlockIds.map(id => [id, toSearchJson(blocks.get(id))])
    ) as SearchBlocksJson,
    children,
    elements: Object.fromEntries(
      pagePlacedElementIds(tree).map(id => [id, toSearchJson(elements.get(id))])
    ) as SearchBlocksJson,
    surfaceElements: pageTreeElementLists(tree),
    members: pageTreeMemberLists(tree),
  };
  const hidden = hiddenBlockIds(page);
  const databaseRows: Record<string, SerializedCells> = {};
  for (const blockId of placedBlockIds) {
    const block = blocks.get(blockId);
    if (
      !block ||
      hidden.has(blockId) ||
      block.get('sys:flavour') !== 'affine:database'
    ) {
      continue;
    }
    // Row-sharded databases keep rows in subdocs; the owning-doc
    // `prop:cells` map is empty by design. The caller injects a
    // `shardRowLoader` that resolves cells from the storage layer
    // (frontend nbstore subdocs, backend service, etc.). Manifest absent =
    // non-sharded legacy doc — read `prop:cells` directly.
    let cells: SerializedCells;
    if (readDatabaseShardDirectoryBlockHead(block)) {
      if (!shardRowLoader) {
        throw new Error(
          `readAllBlocksFromDoc: sharded database requires shardRowLoader; blockId=${blockId}`
        );
      }
      cells = await shardRowLoader(blockId, block);
    } else {
      const cellsObj = block.get('prop:cells');
      cells =
        cellsObj instanceof YMap
          ? ((cellsObj.toJSON() as SerializedCells | undefined) ?? {})
          : {};
    }
    const columns = block.get('prop:columns');
    databaseRows[blockId] = readerDatabaseCells(
      columns instanceof YArray ? columns.toJSON() : [],
      cells
    );
  }

  const extraction = extractSearchBlocks(page, {
    maxSummaryLength,
    databaseRows,
  });
  docTitle = extraction.title;
  summary = extraction.summary;

  // The reader puts the row values of a database into its block document.
  const cellTexts = new Map<string, string[]>();
  for (const cell of extraction.cells) {
    const texts = cellTexts.get(cell.databaseBlockId) ?? [];
    texts.push(cell.text);
    cellTexts.set(cell.databaseBlockId, texts);
  }

  for (const searchBlock of extraction.blocks) {
    const yblock = searchYBlock(blocks, elements, searchBlock);
    if (!yblock) {
      continue;
    }
    const params = referenceParams(yblock);
    const refDocId = searchBlock.refDocId ?? [];
    blockDocuments.push({
      docId: source.head.guid,
      blockId: searchBlock.blockId,
      flavour: searchBlock.flavour,
      sourceType: classifyBlockSourceType(searchBlock.flavour),
      content:
        searchBlock.flavour === 'affine:database'
          ? uniq([
              ...searchBlock.content,
              ...(cellTexts.get(searchBlock.blockId) ?? []),
            ])
          : searchBlock.content,
      ...(searchBlock.blob && { blob: searchBlock.blob }),
      ...(refDocId.length > 0 && {
        refDocId,
        ref: refDocId.map(docId =>
          JSON.stringify({ docId, ...params.get(docId) })
        ),
      }),
      ...(searchBlock.parentBlockId && {
        parentBlockId: searchBlock.parentBlockId,
        parentFlavour: searchBlock.parentFlavour,
      }),
      additional: searchBlock.additional,
      yblock,
    });
  }
  // #endregion

  // #region second loop - generate markdown preview
  const TARGET_PREVIEW_CHARACTER = 500;
  const TARGET_PREVIOUS_BLOCK = 1;
  const TARGET_FOLLOW_BLOCK = 4;
  for (const block of blockDocuments) {
    if (block.ref?.length) {
      const target = block;

      // should only generate the markdown preview belong to the same affine:note
      const noteBlock = nearestByFlavour(block.blockId, 'affine:note');

      const sameNoteBlocks = noteBlock
        ? blockDocuments.filter(
            candidate =>
              nearestByFlavour(candidate.blockId, 'affine:note') === noteBlock
          )
        : [];

      // only generate markdown preview for reference blocks
      let previewText = (await generateMarkdownPreview(target)) ?? '';
      let previousBlock = 0;
      let followBlock = 0;
      let previousIndex = sameNoteBlocks.findIndex(
        block => block.blockId === target.blockId
      );
      let followIndex = previousIndex;

      while (
        !(
          (
            previewText.length > TARGET_PREVIEW_CHARACTER || // stop if preview text reaches the limit
            ((previousBlock >= TARGET_PREVIOUS_BLOCK || previousIndex < 0) &&
              (followBlock >= TARGET_FOLLOW_BLOCK ||
                followIndex >= sameNoteBlocks.length))
          ) // stop if no more blocks, or preview block reaches the limit
        )
      ) {
        if (previousBlock < TARGET_PREVIOUS_BLOCK) {
          previousIndex--;
          const block =
            previousIndex >= 0 ? sameNoteBlocks.at(previousIndex) : null;
          const markdown = block ? await generateMarkdownPreview(block) : null;
          if (
            markdown &&
            !previewText.startsWith(
              markdown
            ) /* A small hack to skip blocks with the same content */
          ) {
            previewText = markdown + '\n' + previewText;
            previousBlock++;
          }
        }

        if (followBlock < TARGET_FOLLOW_BLOCK) {
          followIndex++;
          const block = sameNoteBlocks.at(followIndex);
          const markdown = block ? await generateMarkdownPreview(block) : null;
          if (
            markdown &&
            !previewText.endsWith(
              markdown
            ) /* A small hack to skip blocks with the same content */
          ) {
            previewText = previewText + '\n' + markdown;
            followBlock++;
          }
        }
      }

      block.markdownPreview = unindentMarkdown(previewText);
    }
  }
  // #endregion

  return {
    blocks: blockDocuments,
    title: docTitle,
    summary,
  };
}

export { parseBlock, parseBlockToMd, parsePageDoc } from './doc-parser/parser';
export { readAllDocIdsFromRootDoc, readAllDocsFromRootDoc } from './root-doc';
